<script setup>
// Save a Seat refused because the session clashes with something already on My
// Schedule (Nick, Asana 1217708959827728 — Figma 4755:113828 for a single
// conflict and 4755:112345 for multiple).
//
// The clash has to be settled BEFORE a seat can be held: reserving a seat for a
// session you cannot attend is worse than refusing, because it also takes the
// seat away from someone who could. So this replaces the reservation rather
// than warning alongside it.
//
// The two comps are the same screen apart from the final clause of the notice,
// which turns on whether removing THIS session would settle the schedule — the
// same distinction the removal confirmation uses.
import { modal_button_filled_2026, modal_button_outline_2026, modal_close_link_2026 } from '~~/shared/utilities/ui';

const props = defineProps({
    sessionTitle: { type: String, default: '' },
    // true  — removing this session leaves NO conflicts anywhere
    // false — other conflicting items would remain
    resolves: { type: Boolean, default: false },
});

const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

const result = ref(null);

const quoted = computed(() => `“${props.sessionTitle}”`);

// Verbatim from both comps — identical in each.
const headline = computed(() =>
    `We can’t save a seat for ${quoted.value} because this session is in the same time slot as other items on My Schedule.`
);

// Shared instruction, then the clause that differs. Removing this session is
// offered as a way out either way, but only the single-conflict comp promises
// it settles everything.
const NOTICE_LEAD = 'In order to save a seat for this session, you’ll first need to remove conflicting sessions from My Schedule or delete/edit conflicting events so that there are no overlapping items on My Schedule. You can also choose to remove this session from My Schedule';
const notice = computed(() => (props.resolves
    ? `${NOTICE_LEAD} which will resolve the issue.`
    : `${NOTICE_LEAD}, although this will not fully resolve the issue as there are additional items in conflict.`
));

const choose = (value) => {
    result.value = value;
    open.value = false;
};

// Resolves through emit('close', value) like the other dialogs in this flow, so
// the opener owns what happens next and this stays presentational.
const onShellClose = () => emit('close', result.value);
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        :title="headline"
        @close="onShellClose"
    >
        <div class="flex flex-col items-center gap-[45px] w-full">
            <h2 class="m-0 w-full text-center font-hubspot-serif font-normal text-[25px] sm:text-[30px] leading-[1.3] tracking-normal text-(--magenta)">
                {{ headline }}
            </h2>

            <div
                data-test="seat-blocked-notice"
                class="flex items-start gap-[14px] w-full text-left bg-white rounded-[10px] px-[28px] py-[25px]"
            >
                <svg class="shrink-0 mt-[4px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="m-0 font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta)">
                    {{ notice }}
                </p>
            </div>

            <!-- Learn More leads, because seeing the clash is what makes the
                 instruction above actionable. Removing this session is the
                 shortcut the notice offers, so it sits beside it. -->
            <div class="flex flex-wrap items-start justify-center gap-[20px]">
                <button
                    type="button"
                    data-test="seat-blocked-learn-more"
                    :class="modal_button_filled_2026"
                    @click="choose('learn')"
                >Learn More</button>
                <button
                    type="button"
                    data-test="seat-blocked-remove"
                    :class="modal_button_outline_2026"
                    @click="choose('remove')"
                >Remove From My Schedule</button>
            </div>
        </div>

        <template #actions>
            <button
                type="button"
                data-test="seat-blocked-close"
                :class="modal_close_link_2026"
                @click="choose(null)"
            >Close</button>
        </template>
    </FragmentsModalShell>
</template>
